import { useState, useEffect, useRef } from "react"
import { useNavigate } from "react-router-dom";
import { api, getAuthHeaders } from "../utils/api";
import Layout from '../components/Layout';
import "../styles/my-assets-liability.scss";
import "../styles/shared-with-you.scss"
import Loader from "../components/Loader";
import TruncatedTooltipText from "../components/TruncatedTooltipText";

const SharedWithYou = () => {
  const navigate = useNavigate();
  const [activeFilter, setActiveFilter] = useState("all")
  const [searchTerm, setSearchTerm] = useState("")
  const [viewMode, setViewMode] = useState("grid")
  const [page, setPage] = useState(1);
  const [sharedItems, setSharedItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totalPages, setTotalPages] = useState(1);
  const [showItemModal, setShowItemModal] = useState(false);
  const [activeSharedItem, setActiveSharedItem] = useState(null);

  const searchTimeout = useRef();

  // Fetch shared items
  useEffect(() => {
    const fetchSharedItems = async () => {
      setLoading(true);
      try {
        let url = `/sharing/assets/shared-with-me`;
        
        if (searchTerm && searchTerm.trim().length >= 3) {
          url += `&search=${encodeURIComponent(searchTerm)}`;
        }
        
        if (activeFilter === 'assets') {
          url += `&itemType=asset`;
        } else if (activeFilter === 'liability') {
          url += `&itemType=liability`;
        }

        const response = await api.get(url, { headers: getAuthHeaders() });
        const data = response.data?.results || response.data || [];
        setSharedItems(Array.isArray(data) ? data : []);
        setTotalPages(response.data?.totalPages || 1);
        setError(null);
      } catch (e) {
        console.error('Failed to fetch shared items:', e);
        setError(e?.message || 'Failed to load shared items');
        setSharedItems([]);
      } finally {
        setLoading(false);
      }
    };

    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    
    if (searchTerm && searchTerm.trim().length >= 3) {
      searchTimeout.current = setTimeout(() => {
        fetchSharedItems();
      }, 500);
    } else if (!searchTerm || searchTerm.trim().length === 0) {
      fetchSharedItems();
    }

    return () => clearTimeout(searchTimeout.current);
  }, [page, searchTerm, activeFilter]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) setPage(newPage);
  };
  const filteredItems = sharedItems && sharedItems.length > 0 ? sharedItems.filter((item) => {
    const name = (item.name || "").toLowerCase();
    const type = (item.assetType || "").toLowerCase();
    const matchesSearch =
      name.includes((searchTerm || "").toLowerCase()) ||
      type.includes((searchTerm || "").toLowerCase());

    return matchesSearch;
  }) : [];

  const getPerformanceColor = (percentage) => {
    if (typeof percentage !== "number") return "text-muted";
    if (percentage > 10) return "text-success";
    if (percentage < 0) return "text-danger";
    return "text-warning";
  };

  const getStatusBadge = (status) => {
    switch ((status || "").toLowerCase()) {
      case "active":
        return "badge bg-success"
      case "inactive":
        return "badge bg-secondary"
      case "pending":
        return "badge bg-warning"
      default:
        return "badge bg-primary"
    }
  }

  const formatUpdatedAt = (updatedAt) => {
    if (!updatedAt) return "";
    const updatedDate = new Date(updatedAt);
    const now = new Date();
    const diffMs = now - updatedDate;
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays >= 1) {
      return ` ${diffDays} day${diffDays > 1 ? "s" : ""} ago`;
    } else if (diffHours >= 1) {
      return ` ${diffHours} hr ago`;
    } else {
      return ` ${diffMinutes} min ago`;
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '-';
    try {
      const d = new Date(iso);
      return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
    } catch (e) {
      return iso;
    }
  };

  const formatDateTime = (iso) => {
    if (!iso) return '-';
    try {
      const d = new Date(iso);
      return d.toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      });
    } catch (e) {
      return iso;
    }
  };

  const openItemModal = async (itemOrId) => {
    // If caller passes full item object, use it directly
    if (itemOrId && typeof itemOrId === 'object') {
      setActiveSharedItem(itemOrId);
      setShowItemModal(true);
      return;
    }

    const itemId = itemOrId;
    // try to find item in already-fetched list first (compare as strings to avoid type issues)
    const found = sharedItems.find(i => String(i.id || i._id || i.sharedId) === String(itemId));
    if (found) {
      setActiveSharedItem(found);
      setShowItemModal(true);
      return;
    }

    // fallback: fetch single item detail from API if endpoint exists
    try {
      setLoading(true);
      const res = await api.get(`/sharing/assets/shared-with-me/${itemId}`, { headers: getAuthHeaders() });
      setActiveSharedItem(res.data || null);
      setShowItemModal(true);
    } catch (e) {
      console.error('Failed to fetch shared item detail:', e);
      setError(e?.message || 'Failed to load item details');
    } finally {
      setLoading(false);
    }
  };

  const closeItemModal = () => {
    setShowItemModal(false);
    setActiveSharedItem(null);
  };

  return (
    <Layout>
      <div className="container-fluid px-0">
        <div className={`assets-liability-dashboard shared-dashboard ${viewMode === 'list' ? 'list-view' : ''}`}>
            <div className="row mb-4">
            <div className="col-12">
              <div className="filter-bar">
                <div className="filter-left">
                  <div className="items-filters">
                    <button
                      className={`filter-btn ${activeFilter === "all" ? "active" : ""}`}
                      onClick={() => setActiveFilter("all")}
                    >
                      All <span className="filter-count">{sharedItems.length}</span>
                    </button>
                  </div>
                </div>

                <div className="search-container filter-search">
                  <i className="bi bi-search search-icon"></i>
                  <input
                    type="text"
                    className="form-control search-input"
                    placeholder="Search..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>

                <button
                  className="action-btn"
                  onClick={() => setViewMode(viewMode === "grid" ? "list" : "grid")}
                  title={`Switch to ${viewMode === "grid" ? "list" : "grid"} view`}
                >
                  <i className={`bi ${viewMode === "grid" ? "bi-list-stars" : "bi-grid-3x3-gap"}`}></i>
                </button>
              </div>
            </div>
          </div>

          {loading && <Loader />}

          {viewMode === "grid" ? (
            <div className="row">
              {filteredItems.map((item) => (
                <div key={item.id} className="col-xl-4 col-lg-6 col-md-6 mb-4">
                  <div className="item-card">
                    <div className="item-card-body">
                      <div className="item-card-header">
                        <TruncatedTooltipText
                          text={item?.asset?.name || ""}
                          className="item-card-title-value"
                          Tag="h5"
                          onlyWhenTruncated
                        />
                      </div>

                      <div className="item-badges">
                        <span className={`item-type-badge ${(item?.asset?.assetCategory || "").toLowerCase()}`}>{item?.asset?.assetCategory}</span>
                        <span className="category-badge">
                          <i className="bi bi-tag"></i>
                          {item?.asset?.assetType}
                        </span>
                      </div>
                      {/* <div className="item-performance">
                        <i className="bi bi-graph-up"></i>
                        <span className="performance-title">Performance: </span>
                        <span className={getPerformanceColor(item?.performance?.percentage)}>
                          {typeof item?.performance?.percentage === "number"
                            ? `${item?.performance?.percentage}%`
                            : "0"}
                          {typeof item?.performance?.percentage === "number" && item?.performance?.percentage > 0 && (
                            <i className="bi bi-arrow-up"></i>
                          )}
                          {typeof item?.performance?.percentage === "number" && item?.performance?.percentage < 0 && (
                            <i className="bi bi-arrow-down"></i>
                          )}
                        </span>
                      </div> */}
                      <div className="item-stats">
                        <div className="stat-left">
                          <div className="stat-item pill active stat-status">
                            <i className="bi bi-circle-fill"></i>
                            <span className="status-value">{item?.asset?.status}</span>
                          </div>
                          <div className="stat-item pill">
                            <i className="bi bi-paperclip"></i>
                            <span>{((item?.photos?.length || 0) + (item?.documents?.length || 0))} Attachments</span>
                          </div>
                        </div>
                      </div>
                      <div className="item-card-dates">
                        <div className="stat-item">
                          <i className="bi bi-clock"></i>
                          <span>Updated: {formatUpdatedAt(item?.asset?.purchaseDate)}</span>
                        </div>
                        <div className="stat-item stat-item-shared-date">
                          <i className="bi bi-share"></i>
                          <span>Shared: {formatDateTime(item?.sharedAt)}</span>
                        </div>
                      </div>
                    </div>

                      <div className="item-card-footer">
                      <button className="btn btn-primary btn-sm" onClick={() => openItemModal(item)}>
                          View Details
                           <i className="bi bi-arrow-right"></i>
                        </button>
                      {/* <button className="btn btn-primary btn-sm" onClick={() => navigate(`/shared-with-you/${item?.id}`)}>
                        Open Page
                        <i className="bi bi-arrow-right"></i>
                      </button> */}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="items-table-container">
              <div className="table-responsive">
                <table className="table items-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Type</th>
                      <th>Category</th>
                      <th>Performance</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredItems.map((item) => (
                      <tr key={item.id}>
                        <td>
                          <div className="item-name-cell">
                            <div className="item-info">
                              <div className="name">{item?.asset?.name}</div>
                              <div className="meta">
                                <span>Updated: {formatUpdatedAt(item?.asset?.purchaseDate)} </span>
                                <span>Shared: {formatDateTime(item?.sharedAt)}</span>
                              </div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <span className={`item-type-badge ${(item?.asset?.assetCategory || "").toLowerCase()}`}>{item?.asset?.assetCategory}</span>
                        </td>
                        <td style={{ textTransform: "capitalize" }}>{item?.asset?.assetType}</td>
                        <td>
                          <span className={getPerformanceColor(item.performance?.percentage)}>
                            {typeof item.performance?.percentage === "number"
                              ? `${item.performance.percentage}%`
                              : "0"}
                            {typeof item.performance?.percentage === "number" && item?.performance?.percentage > 0 && (
                              <i className="bi bi-arrow-up"></i>
                            )}
                            {typeof item.performance?.percentage === "number" && item?.performance?.percentage < 0 && (
                              <i className="bi bi-arrow-down"></i>
                            )}
                          </span>
                        </td>
                        <td>
                          <span className={getStatusBadge(item?.asset?.status)}>{item?.asset?.status}</span>
                        </td>
                        <td>
                            <div className="table-actions">
                            <button className="action-icon view-btn" title="View" onClick={() => openItemModal(item)}>
                              <i className="bi bi-eye"></i>
                            </button>
                            {/* <button className="action-icon view-btn" title="Open Page" onClick={() => navigate(`/shared-with-you/${item.id}`)}>
                              <i className="bi bi-arrow-right"></i>
                            </button> */}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {error && (
            <div className="error-state">
              <div className="alert alert-danger" role="alert">
                {error}
              </div>
            </div>
          )}

          {!loading && filteredItems.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">
                <i className="bi bi-inbox"></i>
              </div>
              <h5>No shared items found</h5>
              <p>Items shared with you will appear here</p>
            </div>
          ) : (
            <div className="pagination-controls pagination-sticky" style={{ display: 'flex', justifyContent: 'center', gap: 8 }}>
              <button
                className="btn-sm pagination-text"
                onClick={() => handlePageChange(page - 1)}
                disabled={page === 1}
              >
                Previous
              </button>
              {Array.from({ length: totalPages }, (_, idx) => idx + 1).map((pg) => (
                <button
                  key={pg}
                  className={`btn btn-sm paginations-list ${pg === page ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => handlePageChange(pg)}
                  disabled={pg === page}
                >
                  {pg}
                </button>
              ))}
              <button
                className="btn-sm pagination-text"
                onClick={() => handlePageChange(page + 1)}
                disabled={page === totalPages}
              >
                Next
              </button>
              <span style={{ alignSelf: 'center', fontWeight: 500, marginLeft: 12 }}>
                of {totalPages} pages
              </span>
            </div>
          )}

        {/* Item details modal */}
        {showItemModal && (
          <div className="share-modal-overlay" onClick={closeItemModal}>
            <div className="share-collaborator-modal" onClick={(e) => e.stopPropagation()}>
              <button className="share-modal-close-btn" onClick={closeItemModal}>
                <i className="bi bi-x"></i>
              </button>
              <div className="share-modal-header">
                <h2 className="share-modal-title">Shared Item Details</h2>
              </div>
              <div className="share-modal-body">
                {activeSharedItem ? (
                  <div className="shared-item-details">
                    <h3 className="share-modal-title">Asset Details</h3>
                    <div className="detail-row"><strong>Name:</strong> <span>{activeSharedItem.asset?._id ? activeSharedItem.asset.name : activeSharedItem.asset?.name || '-'}</span></div>
                    <div className="detail-row"><strong>Category:</strong> <span style={{ textTransform: 'capitalize' }}>{activeSharedItem.asset?.assetCategory || '-'}</span></div>
                    <div className="detail-row"><strong>Type:</strong> <span style={{ textTransform: 'capitalize' }}>{activeSharedItem.asset?.assetType || '-'}</span></div>
                    <div className="detail-row"><strong>Purchase Date:</strong> <span>{formatDate(activeSharedItem.asset?.purchaseDate)}</span></div>
                    <div className="detail-row"><strong>Notes:</strong> <span>{activeSharedItem.asset?.notes || '-'}</span></div>
                    <div className="detail-row"><strong>Attachments:</strong> <span>{((activeSharedItem.asset?.photos?.length || 0) + (activeSharedItem.asset?.documents?.length || 0))}</span></div>

                    <h3 className="share-modal-title">Owner Details</h3>
                    <div className="detail-row"><strong>Name:</strong> <span>{activeSharedItem.owner?.name || activeSharedItem.owner?.fullName || '-'}</span></div>
                    <div className="detail-row"><strong>Email:</strong> <span>{activeSharedItem.owner?.email || '-'}</span></div>
                    {/* <hr /> */}
                    {/* <pre className="shared-item-json" style={{whiteSpace: 'pre-wrap', maxHeight: 200, overflow: 'auto'}}>{JSON.stringify(activeSharedItem, null, 2)}</pre> */}
                  </div>
                ) : (
                  <div>Loading...</div>
                )}
              </div>
            </div>
          </div>
        )}

        </div>
      </div>
    </Layout>
  )
}

export default SharedWithYou